import { type WebPushSubscription } from '@campai/lib-common/entities'

import { env } from '@/env'
import { client } from '@/utils/trpc'

const serviceWorkerPath = '/push-service-worker.js'

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
}

export const canUseWebPush = () =>
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window

export const getWebPushPermission = (): NotificationPermission | 'unsupported' => {
    if (!canUseWebPush()) {
        return 'unsupported'
    }

    return Notification.permission
}

export const requestWebPushPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!canUseWebPush()) {
        console.warn('Browser does not support web push notifications')
        return 'unsupported'
    }

    return Notification.requestPermission()
}

export const registerWebPushServiceWorker = async () => {
    if (!canUseWebPush()) {
        console.warn('Browser does not support web push notifications')
        return
    }

    if (Notification.permission !== 'granted') {
        return
    }

    const serviceWorker = await navigator.serviceWorker.register(serviceWorkerPath)
    void serviceWorker.update()

    let subscription = await serviceWorker.pushManager.getSubscription()

    if (!subscription) {
        const publicKey = env.VITE_WEB_PUSH_PUBLIC_KEY

        if (!publicKey) {
            console.error('Web push public key not found')
            return
        }

        subscription = await serviceWorker.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        })
    }

    await client.currentUser.updateWebPush.mutate(subscription.toJSON() as WebPushSubscription)
}
